import * as Yup from 'yup';
import {
    stringWithNoSpecialChars,
    validateDate,
    validateNumber,
    validateMediaType,
    validateOptionalUrl,
    validateOptionalNumber,
    validateActivityPriority,
    validateActivityStatus,
    validateFloat,
    validateFileByType
} from "@/Shared/Helpers";

import {
    ActivityEnums,
    MediaURL,
    UPLOAD_ALLOWED_MEDIA_FILE_TYPES,
    UPLOAD_MAX_FILES,
    UPLOAD_MAX_MEDIA_SIZE,
    UPLOAD_MAX_THUMB_SIZE,
    UploadedMedia
} from '@/Shared/Types';


/**
 * =====================================================
 * Activity Form Validation Schema
 * =====================================================
 *
 * Defines the validation rules for Activity creation and edit forms.
 *
 * Responsibilities:
 * - Ensure all required fields are present and correctly formatted
 * - Validate dates, numbers, enums, and optional text fields
 * - Handle media file uploads: type, size, and optional captions
 * - Limit the number of uploaded media files to configured maximum
 *
 * Notes:
 * - Accepts viewEnums to dynamically validate priority and status
 * - Supports images and videos with separate validation rules
 *
 * Author: Samano CASTRE
 * Date: January 31, 2026
 */

export type ActivityFormValues = {
    id?: number,
    taskId: number,
    startDate: string,
    dueDate: string,
    assignedTo: string,
    initiatorId: string,
    priority: string,
    status: string,
    completionRate: number,
    expenseNote: number,
    description: string,
    images: UploadedMedia[],
    videos: MediaURL[],
    videoURLs: string[];
};

// Media validation options
const mediaFileOptions = {
    maxSizeInMB: UPLOAD_MAX_MEDIA_SIZE,
    allowedTypes: UPLOAD_ALLOWED_MEDIA_FILE_TYPES
};
const mediaThumbOptions = {
    maxSizeInMB: UPLOAD_MAX_THUMB_SIZE,
    allowedTypes: UPLOAD_ALLOWED_MEDIA_FILE_TYPES
};

/**
 * Returns a Yup validation schema for Activity forms
 * @param viewEnums Activity-related enums for status and priority validation
 */
export const ActivityValidationSchema = (viewEnums: ActivityEnums) => Yup.object({

    taskId: validateNumber(),

    startDate: validateDate().required('La date de début est requise'),

    dueDate: validateDate().required("L'échéance est requise"),

    assignedTo: validateNumber(),

    initiatorId: validateNumber(),

    priority: validateActivityPriority(viewEnums.ActivityPriorityEnum),

    status: validateActivityStatus(viewEnums.ActivityStatusEnum),

    completionRate: validateNumber(),

    expenseNote: validateFloat(),

    description: stringWithNoSpecialChars({ minLength: 10, maxLength: 500 })
        .transform((value, originalValue) => originalValue === '' ? null : value).notRequired(),

    images: Yup.array().of(
        Yup.object({
            id: validateOptionalNumber(),
            large: validateFileByType(mediaFileOptions),
            small: validateFileByType(mediaThumbOptions),
            type: validateMediaType('image'),
            caption: stringWithNoSpecialChars({ minLength: 5, maxLength: 255 }).notRequired().nonNullable(),
        })
    ).max(UPLOAD_MAX_FILES, `Maximum de ${UPLOAD_MAX_FILES} images`).notRequired().nonNullable(),

    videos: Yup.array().of(
        Yup.object({
            id: validateOptionalNumber(),
            url: validateOptionalUrl(),
            small: validateFileByType(mediaThumbOptions),
            type: validateMediaType('video'),
            caption: stringWithNoSpecialChars({ minLength: 5, maxLength: 255 }).notRequired().nonNullable(),
        })
    ).max(UPLOAD_MAX_FILES, `Maximum de ${UPLOAD_MAX_FILES} images`).notRequired().nonNullable(),
});
